import { FC, useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  Tooltip
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { saveAs } from 'file-saver';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetVendorReportsQuery } from '@/store/slices/api/reportsApiSlice.ts';
import { setResetVendorPDFsUrls } from '@/store/slices/transmissionReports/transmissionReportsSlice';
import withErrorBoundary from '@/utils/withErrorBoundary.tsx';
import useViewAndDownloadReports from '@/hooks/useViewAndDownloadReports.ts';
import InfoMessage from '@/components/InfoMessage';

import { getReportDownloadName } from '@/pages/physician/ReviewTransmissionReportsPage/TransmissionReportDetails/ActionBar/PrintReport/constants';
import { Patient, ViewAndDownloadReport } from '@/types/patient.ts';
import { SignatureStatus } from '@/types/transmissionReport.ts';

type ViewAndDownloadModalProps = {
  report: ViewAndDownloadReport;
  isCombinedPdf: boolean;
  handleDownloadReport: (reportId: number | null) => void;
  patient: Patient;
};

const ViewAndDownloadModal: FC<ViewAndDownloadModalProps> = ({
  patient,
  handleDownloadReport,
  report,
  isCombinedPdf
}) => {
  const [withVendorReports, setWithVendorReports] = useState(true);
  const [mergedPdfUrl, setMergedPdfUrl] = useState<string | null>(null);

  const { resetVendorPDFsUrls } = useAppSelector((state) => state.transmissionReports);
  const dispatch = useAppDispatch();
  const { isLoading, data, refetch } = useGetVendorReportsQuery(report.id);

  useEffect(() => {
    if (resetVendorPDFsUrls) {
      refetch();
      dispatch(setResetVendorPDFsUrls(false));
    }
  }, [resetVendorPDFsUrls, refetch, dispatch]);

  const { processedReports, isProcessing, handleMergePDFs } = useViewAndDownloadReports({
    vendorReports: data?.vendorTransmissionReports,
    pdfKey: report?.PDFUploadKey,
    signatureStatus: report?.signatureStatus,
    isCombinedPdf,
    withVendorReports
  });

  const isSignedAndCombinedPdf =
    report?.signatureStatus === SignatureStatus.Signed && isCombinedPdf;
  const isDismissedOrNoSign =
    report?.signatureStatus === SignatureStatus.Dismissed ||
    report?.signatureStatus === SignatureStatus.SignatureNotRequired;

  // Generate merged PDF whenever the report changes.
  useEffect(() => {
    if (!report) return;

    const generateMergedPdf = async () => {
      const mergedPdfBuffer = await handleMergePDFs();
      if (mergedPdfBuffer) {
        const blob = new Blob([mergedPdfBuffer], { type: 'application/pdf' });
        setMergedPdfUrl(URL.createObjectURL(blob));
      }
    };

    generateMergedPdf();

    return () => {
      if (mergedPdfUrl) {
        URL.revokeObjectURL(mergedPdfUrl);
        setMergedPdfUrl(null);
      }
    };
    // Exclude mergedPdfUrl from the dependencies to prevent infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, handleMergePDFs]);

  // Handle downloading the merged PDF.
  const handleDownload = async () => {
    if (mergedPdfUrl) {
      const blob = await fetch(mergedPdfUrl).then((res) => res.blob());
      saveAs(
        blob,
        getReportDownloadName(
          report.transmissionType,
          patient.givenName,
          patient.familyName,
          report.createdAt,
          report.reportType === 'HEART_FAILURE'
        )
      );
    }
  };

  // Render content inside the modal.
  const renderContent = () => {
    if (isLoading || isProcessing) {
      return <Skeleton height="40rem" />;
    }
    if (processedReports.length === 0) {
      return (
        <InfoMessage
          title="No Vendor Reports Found"
          message="We couldn't find any vendor reports for this transmission."
        />
      );
    }

    if (isSignedAndCombinedPdf && (!report.PDFUploadKey || report.PDFUploadKey.trim() === '')) {
      return (
        <InfoMessage
          title="Report Not Yet Processed"
          message="The combined report is still being processed. Please try again later or refresh the page."
        />
      );
    }

    return (
      <Stack gap="sm">
        {mergedPdfUrl && (
          <iframe
            src={mergedPdfUrl}
            title="Merged PDF"
            width="100%"
            height="600px"
            style={{ border: 'none' }}
          />
        )}
        {!isDismissedOrNoSign &&
          (isSignedAndCombinedPdf ? (
            <Tooltip
              label={
                <Text size="xs">
                  The saved combined PDF is enabled in the account, which will not allow you to
                  disable the vendor report.
                </Text>
              }
              refProp="rootRef"
              w="fit-content"
              position="top-start">
              <Checkbox label="With vendor report" size="xs" checked disabled />
            </Tooltip>
          ) : (
            <Checkbox
              label="With vendor report"
              size="xs"
              checked={withVendorReports}
              onChange={(event) => setWithVendorReports(event.target.checked)}
            />
          ))}
        <Group gap="sm">
          <Button
            size="sm"
            disabled={isProcessing || !mergedPdfUrl}
            loading={isProcessing}
            onClick={handleDownload}
            leftSection={<IconDownload size="16px" />}>
            Download Report
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Modal.Root
      opened={!!report}
      onClose={() => handleDownloadReport(null)}
      size="70rem"
      closeOnClickOutside={false}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header pb={0}>
          <Flex direction="column">
            <Modal.Title fw={700} c="gray.8">
              View & Download Report
            </Modal.Title>
            <Text size="sm" c="gray.6" mt="2px">
              Report Id: {report.id}
            </Text>
          </Flex>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Divider my="xs" />
          {renderContent()}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

const ViewAndDownloadModalWithBoundary = withErrorBoundary(ViewAndDownloadModal);

export default ViewAndDownloadModalWithBoundary;
